/**
 * Campaign brief editor (Messaging OS, meeting 2026-09-18).
 *
 * Owner rule: the instructions that drive a campaign's messages are never
 * hidden. Two tabs edit the same brief:
 *   - Interview: plain questions about the offer, ideal customer, FAQs and
 *     tone. Only the first two are required; anything left empty is left
 *     out of the prompt entirely.
 *   - Prompt: the EXACT text the AI receives, rendered by the same shared
 *     renderCampaignBrief() the workers use, plus the rule toggles (hover
 *     for why each exists). Unanswered required questions show as blanks to
 *     fill in.
 *
 * Used by the create wizard (Messaging step) and by the campaign page's
 * brief dialog for existing campaigns.
 */

import { useMemo, useState } from 'react'
import { Info, Plus, Trash2, Wand2, AlertCircle } from 'lucide-react'
import {
  BRIEF_FIELD_MAX,
  BRIEF_RULES,
  FAQ_ANSWER_MAX,
  FAQ_QUESTION_MAX,
  MAX_BRIEF_FAQS,
  missingBriefFields,
  renderCampaignBrief,
  type CampaignBrief,
  type CampaignBriefFaq,
  type CampaignBriefTextField,
  type FaqEntry,
  type ObjectionEntry,
  type OfferConfig,
} from '@dealcloser/shared'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'

type Question = {
  readonly field: CampaignBriefTextField
  readonly question: string
  readonly hint: string
  readonly rows?: number
}

type Section = { readonly title: string; readonly questions: readonly Question[] }

/** The interview. Wording is for people; the prompt headings live in the shared renderer. */
export const BRIEF_SECTIONS: readonly Section[] = [
  {
    title: 'Your offer',
    questions: [
      { field: 'what_you_sell', question: 'What do you sell?', hint: 'One or two sentences, the way you would say it to a friend.' },
      { field: 'who_its_for', question: 'Who is it for?', hint: 'The kind of person and company that buys it.' },
      { field: 'outcome', question: 'What result do customers get?', hint: 'e.g. "cuts payroll admin from 6 hours a week to 1"' },
      { field: 'proof', question: 'What proof do you have?', hint: 'Real numbers, named clients, case studies. The AI will only ever cite what you write here.', rows: 3 },
      { field: 'differentiator', question: 'Why you and not the alternatives?', hint: 'What you can claim that competitors cannot.' },
      { field: 'pricing', question: 'What may the AI say about price?', hint: 'e.g. "from $300/month" or "depends on team size, go through it on a call". Leave empty to never mention it.' },
    ],
  },
  {
    title: 'Your ideal customer',
    questions: [
      { field: 'ideal_roles', question: 'Which roles do you want to talk to?', hint: 'e.g. owners, heads of operations, CFOs' },
      { field: 'ideal_companies', question: 'Which companies?', hint: 'Industry, size, stage, location.' },
      { field: 'must_haves', question: 'What makes someone a great fit?', hint: 'e.g. "runs 5+ locations, hiring now"' },
      { field: 'disqualifiers', question: 'Who is NOT a fit?', hint: 'The AI stays polite and does not pitch these people.' },
    ],
  },
  {
    title: 'How it should sound',
    questions: [
      { field: 'conversation_goal', question: 'How should a good conversation end?', hint: 'e.g. "a 15-minute call" or "they try the free trial"' },
      { field: 'tone', question: 'How should the messages sound?', hint: 'e.g. "friendly peer, casual, no corporate language"' },
      { field: 'never_say', question: 'Anything the AI must never say?', hint: 'Words, claims or topics to avoid.' },
      { field: 'extra_instructions', question: 'Anything else the AI should know?', hint: 'Optional. Leave empty if not.', rows: 3 },
    ],
  },
]

const FIELD_LABEL: Record<string, string> = Object.fromEntries(
  BRIEF_SECTIONS.flatMap((s) => s.questions.map((q) => [q.field, q.question])),
)

const inputClass =
  'w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm placeholder:text-subtle-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'

/** FAQ and objection lists from Settings > Offer, used to draft the brief too. */
export type BriefSources = {
  readonly faqs?: readonly FaqEntry[] | null
  readonly objections?: readonly ObjectionEntry[] | null
}

/** A saved FAQ whose question is about price feeds "What may the AI say about price?". */
const PRICE_QUESTION = /\b(price|prices|pricing|cost|costs|how much|fee|fees)\b/i

/**
 * Draft answers from the account's Offer Settings (the website scrape lands
 * there), saved FAQ and objections. Only empty answers are filled, and the
 * FAQ list only while the brief has none, so nothing the user wrote is ever
 * overwritten. Text is clamped to the brief's own limits so the prompt the
 * user confirms is exactly the one the server stores.
 */
export function briefFromOffer(
  brief: CampaignBrief,
  offer: OfferConfig | null | undefined,
  sources: BriefSources = {},
): CampaignBrief {
  const faqs = (sources.faqs ?? []).filter((f) => f.question.trim() && f.answer.trim())
  const objections = (sources.objections ?? []).filter((o) => o.objection.trim() && o.response.trim())
  if (!offer && faqs.length === 0 && objections.length === 0) return brief
  // Clamped to the same limit the server cuts to (normalizeCampaignBrief).
  const keep = (current: string, next: string | undefined, field: keyof typeof BRIEF_FIELD_MAX) =>
    current.trim() ? current : (next ?? '').trim().slice(0, BRIEF_FIELD_MAX[field])
  const proof = offer
    ? [offer.socialProof, offer.hasCaseStudy ? offer.caseStudyOneLiner : '', offer.authorityProof]
        .map((p) => (p ?? '').trim())
        .filter(Boolean)
        .join('\n')
    : ''
  // The price answer goes to the pricing field, so it is not repeated as a
  // FAQ. When the brief already has its own pricing it stays a FAQ.
  const priceFaq = brief.pricing.trim() ? undefined : faqs.find((f) => PRICE_QUESTION.test(f.question))
  const hasFaqs = brief.faqs.some((f) => f.question.trim() || f.answer.trim())
  const drafted: CampaignBriefFaq[] = [
    ...faqs.filter((f) => f !== priceFaq).map((f) => ({ question: f.question, answer: f.answer })),
    ...objections.map((o) => ({ question: o.objection, answer: o.response })),
  ]
    .slice(0, MAX_BRIEF_FAQS)
    .map((f) => ({
      question: f.question.trim().slice(0, FAQ_QUESTION_MAX),
      answer: f.answer.trim().slice(0, FAQ_ANSWER_MAX),
    }))
  return {
    ...brief,
    what_you_sell: keep(brief.what_you_sell, offer?.coreOfferOneLiner || offer?.offerDescription, 'what_you_sell'),
    who_its_for: keep(brief.who_its_for, offer?.targetAudience, 'who_its_for'),
    outcome: keep(brief.outcome, offer?.primaryPainPoint ? `Solves: ${offer.primaryPainPoint}` : '', 'outcome'),
    proof: keep(brief.proof, proof, 'proof'),
    differentiator: keep(brief.differentiator, offer?.differentiator, 'differentiator'),
    pricing: keep(brief.pricing, priceFaq?.answer, 'pricing'),
    faqs: hasFaqs || drafted.length === 0 ? brief.faqs : drafted,
  }
}

export function CampaignBriefEditor({
  value,
  onChange,
  offer,
  sources,
  confirmSlot,
}: {
  readonly value: CampaignBrief
  readonly onChange: (next: CampaignBrief) => void
  /** Offer Settings, for the "Fill from my offer settings" draft button. */
  readonly offer?: OfferConfig | null
  /** Saved FAQ and objections, drafted into the brief by the same button. */
  readonly sources?: BriefSources
  /** Rendered under the prompt (the wizard's confirm checkbox / the dialog's save button). */
  readonly confirmSlot?: React.ReactNode
}) {
  const [tab, setTab] = useState('interview')
  const [focusField, setFocusField] = useState<string | null>(null)
  const rendered = useMemo(() => renderCampaignBrief(value), [value])
  const missing = useMemo(() => missingBriefFields(value), [value])

  const set = (field: CampaignBriefTextField, text: string) => onChange({ ...value, [field]: text })
  const setFaq = (i: number, key: 'question' | 'answer', text: string) =>
    onChange({ ...value, faqs: value.faqs.map((f, j) => (j === i ? { ...f, [key]: text } : f)) })

  function goFill(field: string) {
    setFocusField(field)
    setTab('interview')
    setTimeout(() => document.getElementById(`brief-${field}`)?.focus(), 50)
  }

  const canDraft =
    Boolean(offer && (offer.coreOfferOneLiner || offer.offerDescription || offer.targetAudience)) ||
    (sources?.faqs ?? []).length > 0 ||
    (sources?.objections ?? []).length > 0

  return (
    <Tabs defaultValue="interview" value={tab} onValueChange={setTab} className="space-y-3">
      <TabsList className="grid w-full grid-cols-2">
        <TabsTrigger value="interview">1. Interview</TabsTrigger>
        <TabsTrigger value="prompt">
          2. Prompt the AI uses
          {missing.length > 0 && <span className="ml-1.5 inline-block h-1.5 w-1.5 rounded-full bg-amber-500 align-middle" aria-label="needs answers" />}
        </TabsTrigger>
      </TabsList>

      <TabsContent value="interview" className="space-y-5">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-xs text-muted-foreground">
            Answer what you can. Only the first two are required, and anything you leave empty is left out of the prompt.
          </p>
          {canDraft && (
            <Button type="button" variant="outline" size="sm" className="h-7 text-xs" onClick={() => onChange(briefFromOffer(value, offer, sources))}>
              <Wand2 className="mr-1.5 h-3.5 w-3.5" />
              Fill from my offer settings
            </Button>
          )}
        </div>

        {BRIEF_SECTIONS.map((section) => (
          <div key={section.title} className="space-y-3">
            <h3 className="text-sm font-semibold">{section.title}</h3>
            {section.questions.map((q) => {
              const required = q.field === 'what_you_sell' || q.field === 'who_its_for'
              return (
                <div key={q.field} className="space-y-1">
                  <Label htmlFor={`brief-${q.field}`} className="text-sm">
                    {q.question}
                    {required && <span className="ml-0.5 text-destructive">*</span>}
                  </Label>
                  <textarea
                    id={`brief-${q.field}`}
                    rows={q.rows ?? 2}
                    value={value[q.field]}
                    onChange={(e) => set(q.field, e.target.value)}
                    placeholder={q.hint}
                    className={cn(inputClass, focusField === q.field && required && !value[q.field].trim() && 'border-amber-500')}
                  />
                </div>
              )
            })}

            {section.title === 'Your ideal customer' && (
              <div className="space-y-2 pt-1">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-semibold">FAQs and objections</h3>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-7 text-xs"
                    disabled={value.faqs.length >= MAX_BRIEF_FAQS}
                    onClick={() => onChange({ ...value, faqs: [...value.faqs, { question: '', answer: '' }] })}
                  >
                    <Plus className="mr-1 h-3.5 w-3.5" /> Add
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  Questions leads ask and the answer you want given. The AI answers in its own words and keeps your facts.
                </p>
                {value.faqs.map((f, i) => (
                  <div key={i} className="space-y-1.5 rounded-md border p-2.5">
                    <div className="flex items-start gap-2">
                      <input
                        value={f.question}
                        onChange={(e) => setFaq(i, 'question', e.target.value)}
                        placeholder='e.g. "Does it work with Toast?"'
                        className={cn(inputClass, 'h-8 py-1')}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 shrink-0 p-0"
                        aria-label="Remove FAQ"
                        onClick={() => onChange({ ...value, faqs: value.faqs.filter((_, j) => j !== i) })}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                    <textarea
                      rows={2}
                      value={f.answer}
                      onChange={(e) => setFaq(i, 'answer', e.target.value)}
                      placeholder="The answer you want given"
                      className={inputClass}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}

        <Button type="button" variant="outline" size="sm" className="w-full" onClick={() => setTab('prompt')}>
          See the prompt the AI will use
        </Button>
      </TabsContent>

      <TabsContent value="prompt" className="space-y-4">
        <div className="space-y-2">
          <h3 className="text-sm font-semibold">Rules for this campaign</h3>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {BRIEF_RULES.map((r) => {
              const on = value.rules[r.id] ?? r.defaultOn
              return (
                <div key={r.id} className="flex items-start gap-2">
                  <Checkbox
                    id={`rule-${r.id}`}
                    checked={on}
                    onCheckedChange={(c) => onChange({ ...value, rules: { ...value.rules, [r.id]: c === true } })}
                    className="mt-0.5"
                  />
                  <Label htmlFor={`rule-${r.id}`} className="text-sm font-normal leading-snug">
                    {r.label}
                  </Label>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button type="button" className="mt-0.5 text-muted-foreground hover:text-foreground" aria-label={`Why: ${r.label}`}>
                        <Info className="h-3.5 w-3.5" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent className="max-w-[260px] text-xs">{r.rationale}</TooltipContent>
                  </Tooltip>
                </div>
              )
            })}
          </div>
        </div>

        {missing.length > 0 && (
          <div className="space-y-1.5 rounded-md border border-amber-500/40 bg-amber-500/10 p-3">
            <p className="flex items-center gap-1.5 text-sm font-medium text-amber-700 dark:text-amber-400">
              <AlertCircle className="h-4 w-4" /> Fill in the blanks before this campaign can send
            </p>
            {missing.map((f) => (
              <button
                key={f}
                type="button"
                onClick={() => goFill(f)}
                className="block text-left text-sm underline underline-offset-2 hover:no-underline"
              >
                [ {FIELD_LABEL[f] ?? f} ]
              </button>
            ))}
          </div>
        )}

        <div className="space-y-1.5">
          <p className="text-xs text-muted-foreground">
            This is the exact text added to the AI&apos;s instructions for this campaign. DealCloser&apos;s core messaging rules
            (message structure, honesty, no AI tells) always apply on top of it.
          </p>
          <pre className="max-h-[340px] overflow-auto whitespace-pre-wrap rounded-md border bg-muted/40 p-3 font-mono text-xs leading-relaxed">
            {rendered || 'Nothing yet. Answer the interview questions and this fills in.'}
          </pre>
        </div>

        {confirmSlot}
      </TabsContent>
    </Tabs>
  )
}
